import React from "react";
import { Box } from "@mui/material";
import { COLORS } from "./constants";

// ─────────────────────────────────────────────────────────────────────────────
// Sparkline
//
// Hand-rolled inline SVG rather than a charting library: recharts was removed
// from this project as an unused 200 kB dependency, and re-adding it to draw
// one 100×28 polyline would undo that. There is no axis, legend, tooltip or
// interaction here — a path and a dot is the whole requirement.
//
// The y-axis is scaled to the window's own min/max rather than to zero, so the
// line shows VARIATION. A bitrate hovering between 5.9 and 6.1 Mbps would be a
// flat line against a zero baseline, which is exactly the wobble an operator
// wants to see. `flat` guards a constant series, where max - min is 0 and the
// naive scale would divide by zero.
// ─────────────────────────────────────────────────────────────────────────────

const W = 100;
const H = 28;
const PAD = 3;

export function Sparkline({ values, color = COLORS.cyan, ariaLabel }) {
  const data = (values || []).filter((v) => typeof v === "number" && !Number.isNaN(v));

  // One point cannot describe a trend; render the empty frame instead of a dot
  // marooned at an arbitrary height.
  if (data.length < 2) {
    return <Box sx={{ width: W, height: H }} aria-hidden="true" />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const flat = max - min < 1e-9;

  const x = (i) => PAD + (i / (data.length - 1)) * (W - PAD * 2);
  const y = (v) => {
    if (flat) return H / 2;
    return H - PAD - ((v - min) / (max - min)) * (H - PAD * 2);
  };

  const points = data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const lastX = x(data.length - 1);
  const lastY = y(data[data.length - 1]);

  return (
    <Box
      component="svg"
      width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none"
      role="img"
      aria-label={ariaLabel || `Trend, ${data.length} samples`}
      sx={{ display: "block", flexShrink: 0 }}
    >
      <polyline
        points={points}
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx={lastX} cy={lastY} r="2" fill={color} />
    </Box>
  );
}
